import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { UploadMetadata } from '../types/uploads';
import { isStripeConfigured } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Upload, Save, Plus, X, CreditCard, AlertTriangle, ArrowLeft } from 'lucide-react';

interface Genre {
  id: string;
  name: string;
  slug: string;
}

interface SeriesOption {
  id: string;
  title: string;
  seasons: { id: string; season_number: number }[];
}

interface ContributorEntry {
  name: string;
  role: string;
  character_name?: string;
}

const CONTENT_TYPES = [
  { value: 'movie', label: 'Movie (Feature Film)' },
  { value: 'short_film', label: 'Short Film (Under 40 min)' },
  { value: 'tv_show', label: 'TV Series Episode' },
  { value: 'documentary', label: 'Documentary' },
  { value: 'bonus', label: 'Behind-the-Scenes / Bonus Content' },
] as const;

const CONTRIBUTOR_ROLES = [
  'Director',
  'Producer',
  'Writer',
  'Actor',
  'Cinematographer',
  'Editor',
  'Composer',
  'Production Designer',
  'Costume Designer',
  'Sound Designer',
] as const;

// Maps the form content type to the valid pending_uploads.content_type DB enum
const toPendingUploadType = (formType: string): 'movie' | 'episode' | 'trailer' => {
  if (formType === 'tv_show' || formType === 'episode') return 'episode';
  if (formType === 'trailer') return 'trailer';
  return 'movie'; // movie, short_film, documentary, bonus
};

// Maps form content type to content_upload_fees.fee_type
const toFeeType = (formType: string): string => {
  if (formType === 'tv_show') return 'episode';
  return formType; // movie, short_film, documentary, episode, bonus
};

// Determine fee based on content type and duration
const calculateFee = (
  contentType: string,
  durationMinutes: number | null
): { amount: number; isFree: boolean; error?: string } => {
  if (contentType === 'bonus') return { amount: 0, isFree: true };

  if (contentType === 'tv_show') {
    if (durationMinutes !== null && durationMinutes > 60) {
      return { amount: 0, isFree: false, error: 'EPISODE_TOO_LONG' };
    }
    if (durationMinutes !== null && durationMinutes <= 35) {
      return { amount: 25, isFree: false };
    }
    return { amount: 50, isFree: false }; // 36–60 min (or unknown duration)
  }

  // movie, short_film, documentary
  return { amount: 100, isFree: false };
};

// Form validation schema
const uploadFormSchema = z.object({
  content_type: z.enum(['movie', 'short_film', 'tv_show', 'documentary', 'bonus', 'episode', 'trailer'] as const),
  title: z.string().min(1, 'Title is required').max(200),
  description: z.string().optional(),
  language: z.string().min(1, 'Language is required'),
  rating: z.enum(['G', 'PG', 'PG-13', 'R', 'NC-17', 'TV-Y', 'TV-G', 'TV-PG', 'TV-14', 'TV-MA'] as const),
  genre_ids: z.array(z.string()).optional(),
  tags: z.string().optional(),
  runtime_claimed: z.number().optional(),
  duration_minutes: z.number().min(1).max(60).optional(),
  season_number: z.number().optional(),
  episode_number: z.number().optional(),
  work_id: z.string().optional(),
  contributors: z.array(z.object({
    name: z.string().min(1, 'Name is required'),
    role: z.string().min(1, 'Role is required'),
    character_name: z.string().optional(),
  })).optional(),
  attestation_rights: z.boolean().refine((val) => val === true, {
    message: 'You must confirm you have distribution rights',
  }),
  attestation_guidelines: z.boolean().refine((val) => val === true, {
    message: 'You must agree to the Content Guidelines and Terms of Service',
  }),
});

type UploadFormValues = z.infer<typeof uploadFormSchema>;

type Step = 'metadata' | 'payment';

export default function ContentUpload() {
  const navigate = useNavigate();
  const { publisher } = useAuth();
  const [searchParams] = useSearchParams();
  const presetWorkId = searchParams.get('work_id') || undefined;
  const presetEpisodeId = searchParams.get('episode_id') || undefined;
  const [step, setStep] = useState<Step>('metadata');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [publisherId, setPublisherId] = useState<string | null>(null);
  const [stripeConnected, setStripeConnected] = useState<boolean | null>(null);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [savedFormValues, setSavedFormValues] = useState<UploadFormValues | null>(null);
  const [seriesList, setSeriesList] = useState<SeriesOption[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<string>('');
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>('');

  const form = useForm<UploadFormValues>({
    resolver: zodResolver(uploadFormSchema),
    defaultValues: {
      content_type: 'movie',
      language: 'en',
      genre_ids: [],
      contributors: [],
      attestation_rights: false,
      attestation_guidelines: false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'contributors',
  });

  const contentType = form.watch('content_type');

  useEffect(() => {
    if (presetWorkId) {
      form.setValue('work_id', presetWorkId);
      if (presetEpisodeId) {
        form.setValue('content_type', 'tv_show');
      }
    }
  }, [presetWorkId, presetEpisodeId, form]);

  // Fetch publisher ID and genres when studio context is ready
  useEffect(() => {
    const fetchData = async () => {
      const { data: genreData } = await supabase
        .from('genres')
        .select('id, name, slug')
        .order('name');

      if (genreData) {
        setGenres(genreData);
      }

      if (!publisher?.id) return;

      setPublisherId(publisher.id);
      setStripeConnected(publisher.stripe_connected === true);

      const { data: seriesData } = await supabase
        .from('works')
        .select('id, title, seasons(id, season_number)')
        .eq('publisher_id', publisher.id)
        .eq('type', 'tv_show')
        .neq('status', 'archived')
        .order('title');
      if (seriesData) setSeriesList(seriesData as SeriesOption[]);
    };
    fetchData();
  }, [publisher?.id, publisher?.stripe_connected]);

  const buildMetadata = (values: UploadFormValues): Partial<UploadMetadata> => {
    const selectedGenres = genres
      .filter(g => values.genre_ids?.includes(g.id))
      .map(g => g.name);

    const metadata: Partial<UploadMetadata> = {
      title: values.title,
      description: values.description,
      language: values.language,
      rating: values.rating,
      tags: values.tags ? values.tags.split(',').map(t => t.trim()) : [],
      genres: selectedGenres,
      runtime_claimed: values.runtime_claimed,
      monetization: 'subscription',
    };

    if (values.content_type === 'tv_show' || values.content_type === 'episode') {
      metadata.season_number = values.season_number;
      metadata.episode_number = values.episode_number;
      metadata.work_id = values.work_id;
      if (selectedSeriesId) (metadata as Record<string, unknown>).show_id = selectedSeriesId;
      if (selectedSeasonId) (metadata as Record<string, unknown>).season_id = selectedSeasonId;
    }

    return metadata;
  };

  const saveContributors = async (
    uploadId: string,
    contributors: ContributorEntry[],
    workId?: string
  ) => {
    if (!contributors || contributors.length === 0) return;

    for (let i = 0; i < contributors.length; i++) {
      const entry = contributors[i];

      let { data: existing } = await supabase
        .from('contributors')
        .select('id')
        .eq('name', entry.name)
        .maybeSingle();

      let contributorId: string;

      if (existing) {
        contributorId = existing.id;
      } else {
        const { data: created, error } = await supabase
          .from('contributors')
          .insert({ name: entry.name })
          .select('id')
          .single();

        if (error || !created) continue;
        contributorId = created.id;
      }

      if (workId) {
        const { error: wcError } = await supabase.from('work_contributors').insert({
          work_id: workId,
          contributor_id: contributorId,
          role: entry.role,
          character_name: entry.character_name || null,
          credit_order: i + 1,
        });
        if (wcError && !wcError.message.toLowerCase().includes('duplicate')) {
          console.warn('work_contributors insert:', wcError.message);
        }
      }

      await supabase.from('upload_events').insert({
        upload_id: uploadId,
        actor_id: publisherId,
        action: 'contributor_added',
        metadata: {
          contributor_id: contributorId,
          role: entry.role,
          character_name: entry.character_name || null,
          credit_order: i + 1,
          work_id: workId ?? null,
        },
      });
    }
  };

  const saveGenreSelections = async (
    uploadId: string,
    genreIds: string[],
    workId?: string
  ) => {
    if (!genreIds || genreIds.length === 0) return;

    if (workId) {
      await supabase.from('work_genres').delete().eq('work_id', workId);
      await supabase.from('work_genres').insert(
        genreIds.map(genreId => ({ work_id: workId, genre_id: genreId }))
      );
    }

    await supabase.from('upload_events').insert({
      upload_id: uploadId,
      actor_id: publisherId,
      action: 'genres_selected',
      metadata: { genre_ids: genreIds, work_id: workId ?? null },
    });
  };

  const createPendingUpload = async (values: UploadFormValues, isDraft: boolean) => {
    if (!publisherId) throw new Error('Publisher not found');

    const metadata = buildMetadata(values);
    const dbContentType = toPendingUploadType(values.content_type);

    const resolvedWorkId = values.work_id || presetWorkId || null;
    const resolvedEpisodeId = presetEpisodeId || null;

    const { data, error } = await supabase
      .from('pending_uploads')
      .insert({
        publisher_id: publisherId,
        content_type: dbContentType,
        upload_status: 'draft',
        work_id: resolvedWorkId,
        episode_id: resolvedEpisodeId,
        metadata: { ...metadata, genre_ids: values.genre_ids },
      })
      .select()
      .single();

    if (error) throw error;

    await supabase.from('upload_events').insert({
      upload_id: data.id,
      actor_id: publisherId,
      action: 'created',
      metadata: { draft: isDraft, ready_for_upload: !isDraft },
    });

    if (values.genre_ids?.length) {
      await saveGenreSelections(data.id, values.genre_ids, resolvedWorkId ?? undefined);
    }
    if (values.contributors?.length) {
      await saveContributors(data.id, values.contributors, resolvedWorkId ?? undefined);
    }

    return data.id as string;
  };

  const onSaveDraft = async (values: UploadFormValues) => {
    setIsSubmitting(true);
    try {
      await createPendingUpload(values, true);
      toast.success('Draft saved successfully');
      navigate('/content/uploads');
    } catch (error: any) {
      console.error('Error saving draft:', error);
      toast.error(error.message || 'Failed to save draft');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (values: UploadFormValues) => {
    // Episode duration validation
    if (values.content_type === 'tv_show') {
      const dur = values.duration_minutes ?? null;
      if (dur !== null && dur > 60) {
        toast.error(
          'Episodes must be 60 minutes or under. For longer content, upload as a movie or documentary.'
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const id = await createPendingUpload(values, false);
      setUploadId(id);
      setSavedFormValues(values);
      setStep('payment');
    } catch (error: any) {
      console.error('Error creating upload:', error);
      toast.error(error.message || 'Failed to create upload');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayFee = async () => {
    if (!publisherId || !uploadId || !savedFormValues) return;

    const dur = savedFormValues.content_type === 'tv_show'
      ? (savedFormValues.duration_minutes ?? null)
      : null;

    const fee = calculateFee(savedFormValues.content_type, dur);

    if (fee.error === 'EPISODE_TOO_LONG') {
      toast.error('Episodes must be 60 minutes or under.');
      setStep('metadata');
      return;
    }

    if (fee.isFree) {
      // Bonus content — no payment needed, go straight to file upload
      toast.success('No upload fee for bonus content. Proceed to upload your file.');
      navigate(`/content/upload/${uploadId}?bonus=true`);
      return;
    }

    if (!fee.isFree && !isStripeConfigured()) {
      toast.error('Stripe is not configured. Add VITE_STRIPE_PUBLISHABLE_KEY to your .env file.');
      return;
    }

    setPaymentLoading(true);
    try {
      const resolvedWorkId = savedFormValues.work_id || presetWorkId || null;
      const resolvedEpisodeId = presetEpisodeId || null;

      // {FEE_ID} is a server-side placeholder — the edge function replaces it with the actual fee ID
      const successUrl = `${window.location.origin}/content/upload/${uploadId}?fee_paid=true&fee_id={FEE_ID}`;
      const cancelUrl = `${window.location.origin}/content/new?cancelled=true`;

      const { data, error } = await supabase.functions.invoke('create-upload-fee-checkout', {
        body: {
          publisher_id: publisherId,
          work_id: resolvedWorkId,
          episode_id: resolvedEpisodeId,
          content_type: toFeeType(savedFormValues.content_type),
          duration_minutes: dur,
          success_url: successUrl,
          cancel_url: cancelUrl,
          pending_upload_id: uploadId,
        },
      });

      if (error) throw error;

      if (data?.free) {
        toast.success('No upload fee required. Proceeding to upload.');
        navigate(`/content/upload/${uploadId}?bonus=true`);
        return;
      }

      if (data?.checkout_url) {
        if (data?.fee_id) {
          localStorage.setItem(`pending_fee_${uploadId}`, data.fee_id);
          // Persist fee_id into the upload record so FileUpload can find it even after
          // localStorage is cleared (e.g. different browser, incognito, returning later).
          const metadata = buildMetadata(savedFormValues);
          supabase.from('pending_uploads')
            .update({ metadata: { ...metadata, genre_ids: savedFormValues.genre_ids, fee_id: data.fee_id } })
            .eq('id', uploadId)
            .then(() => {});
        }
        window.location.href = data.checkout_url;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to start payment. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const selectedGenreIds = form.watch('genre_ids') || [];

  // ── Payment Step ─────────────────────────────────────────────────────────
  if (step === 'payment' && savedFormValues) {
    const dur = savedFormValues.content_type === 'tv_show'
      ? (savedFormValues.duration_minutes ?? null)
      : null;
    const fee = calculateFee(savedFormValues.content_type, dur);
    const feeLabel = CONTENT_TYPES.find(c => c.value === savedFormValues.content_type)?.label ?? savedFormValues.content_type;

    return (
      <div className="container mx-auto py-8 max-w-2xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Upload Fee</h1>
          <p className="text-muted-foreground mt-2">
            Pay the upload fee to proceed to file upload.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Required
            </CardTitle>
            <CardDescription>
              Review the fee for your content before proceeding
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border/60">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Title</span>
                <span className="font-medium">{savedFormValues.title}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Content type</span>
                <span className="font-medium">{feeLabel}</span>
              </div>
              {dur !== null && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium">{dur} minutes</span>
                </div>
              )}
              <div className="border-t border-border/60 pt-3 flex justify-between">
                <span className="font-semibold">Upload fee</span>
                <span className="text-xl font-bold">
                  {fee.isFree ? 'Free' : `$${fee.amount}`}
                </span>
              </div>
            </div>

            {/* Non-refundable notice */}
            <div className="flex items-start gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Non-refundable.</strong> Upload fees cover
                content moderation, encoding, and storage costs and are not refunded if your
                content is rejected.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep('metadata')}
                disabled={paymentLoading}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handlePayFee}
                disabled={paymentLoading}
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redirecting to payment...
                  </>
                ) : fee.isFree ? (
                  'Continue (Free)'
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Pay ${fee.amount} & Continue
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Stripe Connect Gate ───────────────────────────────────────────────────
  if (stripeConnected === false) {
    return (
      <div className="container mx-auto py-8 max-w-2xl">
        <div className="flex flex-col items-center justify-center text-center py-16 space-y-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <CreditCard className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold">Connect Stripe to Upload Content</h2>
          <p className="text-muted-foreground max-w-md">
            You need to connect a Stripe account before uploading content. This allows Kinora
            to collect upload fees and send you payouts for your work.
          </p>
          <Button onClick={() => navigate('/billing')}>
            Go to Billing & Connect Stripe
          </Button>
        </div>
      </div>
    );
  }

  // ── Metadata Step ─────────────────────────────────────────────────────────
  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Upload Content</h1>
        <p className="text-muted-foreground mt-2">
          Fill out the metadata for your content. You'll pay the upload fee before the file upload begins.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Content Information</CardTitle>
              <CardDescription>
                Basic information about your content
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="content_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content Type *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select content type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CONTENT_TYPES.map(ct => (
                          <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter content title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description / Synopsis</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter a brief description of your content"
                        className="resize-none"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {(contentType === 'tv_show' || contentType === 'episode') && (
                <>
                  {/* Series selector */}
                  <FormItem>
                    <FormLabel>TV Series</FormLabel>
                    <Select
                      value={selectedSeriesId}
                      onValueChange={v => { setSelectedSeriesId(v); setSelectedSeasonId(''); }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a TV series" />
                      </SelectTrigger>
                      <SelectContent>
                        {seriesList.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Link this episode to an existing TV series.{' '}
                      <a href="/content/series/new" className="text-primary underline-offset-2 hover:underline">
                        Create a new series
                      </a>
                    </FormDescription>
                  </FormItem>

                  {/* Season selector — shown once a series is picked */}
                  {selectedSeriesId && (() => {
                    const series = seriesList.find(s => s.id === selectedSeriesId);
                    const seasons = series?.seasons ?? [];
                    return seasons.length > 0 ? (
                      <FormItem>
                        <FormLabel>Season</FormLabel>
                        <Select value={selectedSeasonId} onValueChange={setSelectedSeasonId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select season" />
                          </SelectTrigger>
                          <SelectContent>
                            {seasons
                              .sort((a, b) => a.season_number - b.season_number)
                              .map(s => (
                                <SelectItem key={s.id} value={s.id}>
                                  Season {s.season_number}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No seasons found for this series.{' '}
                        <a href={`/content/series/${selectedSeriesId}`} className="text-primary hover:underline">
                          Add a season
                        </a>
                      </p>
                    );
                  })()}

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="season_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Season Number *</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="1"
                              placeholder="1"
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="episode_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Episode Number *</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="1"
                              placeholder="1"
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="duration_minutes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Episode Duration (minutes) *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            max="60"
                            placeholder="45"
                            {...field}
                            onChange={(e) =>
                              field.onChange(e.target.value ? parseInt(e.target.value) : undefined)
                            }
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormDescription>
                          Episodes must be 60 minutes or under. ≤35 min = $25 fee; 36–60 min = $50 fee.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Language *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                        <SelectItem value="ja">Japanese</SelectItem>
                        <SelectItem value="ko">Korean</SelectItem>
                        <SelectItem value="zh">Chinese</SelectItem>
                        <SelectItem value="hi">Hindi</SelectItem>
                        <SelectItem value="pt">Portuguese</SelectItem>
                        <SelectItem value="ar">Arabic</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content Rating *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select rating" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="G">G - General Audiences</SelectItem>
                        <SelectItem value="PG">PG - Parental Guidance</SelectItem>
                        <SelectItem value="PG-13">PG-13 - Parents Strongly Cautioned</SelectItem>
                        <SelectItem value="R">R - Restricted</SelectItem>
                        <SelectItem value="NC-17">NC-17 - Adults Only</SelectItem>
                        <SelectItem value="TV-Y">TV-Y - All Children</SelectItem>
                        <SelectItem value="TV-G">TV-G - General Audience</SelectItem>
                        <SelectItem value="TV-PG">TV-PG - Parental Guidance</SelectItem>
                        <SelectItem value="TV-14">TV-14 - Parents Strongly Cautioned</SelectItem>
                        <SelectItem value="TV-MA">TV-MA - Mature Audience</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Genre Multi-Select */}
              <FormField
                control={form.control}
                name="genre_ids"
                render={() => (
                  <FormItem>
                    <FormLabel>Genres</FormLabel>
                    <FormDescription>
                      Select all genres that apply
                    </FormDescription>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {genres.map((genre) => {
                        const isSelected = selectedGenreIds.includes(genre.id);
                        return (
                          <Badge
                            key={genre.id}
                            variant={isSelected ? 'default' : 'outline'}
                            className={`cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-primary hover:bg-primary/80'
                                : 'hover:bg-muted'
                            }`}
                            onClick={() => {
                              const current = form.getValues('genre_ids') || [];
                              if (isSelected) {
                                form.setValue('genre_ids', current.filter(id => id !== genre.id));
                              } else {
                                form.setValue('genre_ids', [...current, genre.id]);
                              }
                            }}
                          >
                            {genre.name}
                          </Badge>
                        );
                      })}
                    </div>
                    {selectedGenreIds.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {selectedGenreIds.length} genre{selectedGenreIds.length !== 1 ? 's' : ''} selected
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input placeholder="sci-fi, cyberpunk, dystopian (comma-separated)" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter tags separated by commas
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="runtime_claimed"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Runtime (minutes)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        placeholder="90"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) * 60 : undefined)}
                        value={field.value ? Math.floor(field.value / 60) : ''}
                      />
                    </FormControl>
                    <FormDescription>
                      Optional - will be verified against actual file
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Cast & Crew */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Cast & Crew</CardTitle>
                  <CardDescription>
                    Add the people who worked on this content
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ name: '', role: 'Director', character_name: '' })}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Person
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {fields.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No cast or crew added yet. Click "Add Person" to get started.
                </p>
              )}
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-start gap-3 p-3 border rounded-lg">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <FormField
                      control={form.control}
                      name={`contributors.${index}.name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="Full name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`contributors.${index}.role`}
                      render={({ field }) => (
                        <FormItem>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Role" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {CONTRIBUTOR_ROLES.map(role => (
                                <SelectItem key={role} value={role}>{role}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`contributors.${index}.character_name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="Character name (if actor)" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                    className="text-destructive hover:text-destructive mt-1"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Attestations</CardTitle>
              <CardDescription>
                Please confirm the following statements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="attestation_rights"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        I confirm I own or have the rights to distribute this content *
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="attestation_guidelines"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        I agree to the Content Guidelines and Terms of Service *
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={form.handleSubmit(onSaveDraft)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Draft
                </>
              )}
            </Button>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Continue to Payment
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
